import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.isEnabled = process.env.NODE_ENV === 'production';
  }

  // Initialize performance monitoring
  init() {
    if (!this.isEnabled) return;

    // Monitor Core Web Vitals
    getCLS(this.handleMetric.bind(this, 'CLS'));
    getFID(this.handleMetric.bind(this, 'FID'));
    getFCP(this.handleMetric.bind(this, 'FCP'));
    getLCP(this.handleMetric.bind(this, 'LCP'));
    getTTFB(this.handleMetric.bind(this, 'TTFB'));

    // Monitor custom metrics
    this.monitorImageLoading();
    this.monitorBundleSize();
    this.monitorMemoryUsage();
  }

  // Handle individual metrics
  handleMetric(name, metric) {
    this.metrics[name] = metric;
    
    console.log(`${name}:`, metric.value);
    
    // Send to analytics (implement your preferred analytics service)
    this.sendToAnalytics(name, metric);
    
    // Check performance budgets
    this.checkPerformanceBudgets(name, metric.value);
  }

  // Monitor image loading performance
  monitorImageLoading() {
    const images = document.querySelectorAll('img');
    let totalImages = images.length;
    let loadedImages = 0;
    const startTime = performance.now();

    images.forEach(img => {
      if (img.complete) {
        loadedImages++;
      } else {
        img.addEventListener('load', () => {
          loadedImages++;
          if (loadedImages === totalImages) {
            const loadTime = performance.now() - startTime;
            this.handleMetric('AllImagesLoaded', { value: loadTime });
          }
        });
      }
    });
  }

  // Monitor bundle size
  monitorBundleSize() {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      const transferSize = performance.getEntriesByType('navigation')[0]?.transferSize;
      
      if (transferSize) {
        this.handleMetric('TransferSize', { value: transferSize });
        
        // Calculate estimated load time based on connection
        const estimatedLoadTime = transferSize / (connection.downlink * 1000 / 8);
        this.handleMetric('EstimatedLoadTime', { value: estimatedLoadTime });
      }
    }
  }

  // Monitor memory usage
  monitorMemoryUsage() {
    if ('memory' in performance) {
      const memory = performance.memory;
      this.handleMetric('MemoryUsage', {
        value: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        limit: memory.jsHeapSizeLimit
      });
    }
  }

  // Check performance budgets
  checkPerformanceBudgets(metric, value) {
    const budgets = {
      'LCP': 2500, // Largest Contentful Paint should be < 2.5s
      'FID': 100,  // First Input Delay should be < 100ms
      'CLS': 0.1,  // Cumulative Layout Shift should be < 0.1
      'FCP': 1800, // First Contentful Paint should be < 1.8s
      'TTFB': 800, // Time to First Byte should be < 800ms
      'TransferSize': 5000000, // Total transfer size should be < 5MB
      'AllImagesLoaded': 5000   // All images should load within 5s
    };

    const budget = budgets[metric];
    if (budget && value > budget) {
      console.warn(`⚠️ Performance budget exceeded for ${metric}: ${value} > ${budget}`);
      
      // You could trigger alerts, send to monitoring service, etc.
      this.sendPerformanceAlert(metric, value, budget);
    } else if (budget) {
      console.log(`✅ Performance budget met for ${metric}: ${value} <= ${budget}`);
    }
  }

  // Send metrics to analytics service
  sendToAnalytics(name, metric) {
    // Implement your analytics service here
    // Examples: Google Analytics, Adobe Analytics, custom endpoint
    
    if (window.gtag) {
      window.gtag('event', name, {
        event_category: 'Web Vitals',
        value: Math.round(metric.value),
        non_interaction: true,
      });
    }
  }

  // Send performance alerts
  sendPerformanceAlert(metric, value, budget) {
    // Implement alerting here
    // Examples: send to monitoring service, email, Slack, etc.
    
    console.error(`Performance Alert: ${metric} exceeded budget`, {
      metric,
      value,
      budget,
      timestamp: new Date().toISOString()
    });
  }

  // Get current metrics summary
  getMetricsSummary() {
    return {
      ...this.metrics,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      connection: navigator.connection?.effectiveType || 'unknown'
    };
  }

  // Generate performance report
  generateReport() {
    const metrics = this.getMetricsSummary();
    
    console.group('📊 Performance Report');
    console.log('Core Web Vitals:', {
      LCP: metrics.LCP?.value,
      FID: metrics.FID?.value,
      CLS: metrics.CLS?.value
    });
    console.log('Loading Metrics:', {
      FCP: metrics.FCP?.value,
      TTFB: metrics.TTFB?.value,
      TransferSize: metrics.TransferSize?.value
    });
    console.log('Custom Metrics:', {
      AllImagesLoaded: metrics.AllImagesLoaded?.value,
      MemoryUsage: metrics.MemoryUsage?.value
    });
    console.groupEnd();

    return metrics;
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor;
